// @ts-check
import classNames from 'classnames';
import { graphql } from 'gatsby';
import React from 'react';
import { addPx } from '../utils/addPx';
import { getImageDisplayOptions } from '../utils/ImageHelper';

import './BynderSimpleImage.scss';

export const query = graphql`
	fragment BynderImageFragment on Ozk_bynder_image {
		uid
		alt_text
		background_size
		x_position
		y_position
		max_height
		max_width

		transforms {
			transform
			transform_options {
				key
				value
			}
		}

		remoteToLocalImage {
			childImageSharp {
				gatsbyImageData(quality: 100, placeholder: BLURRED, layout: CONSTRAINED)
			}
		}

		image {
			name
			files {
				transformBaseUrl {
					url
				}
			}
		}
	}
`;

/**
 * @typedef BynderSimpleImageProps
 * @property {import('../CSTypes').BynderImageOptions} [option]
 * @property {boolean} [isLivePreviewImage=false]
 * @property {'img' | 'div'} [type='div']
 */
/** @type {import('~/types/React').ReactFC<import('~/contentstack/types').ModelWithLivePreviewAttr<BynderSimpleImageProps>>} */
const BynderSimpleImage = ({
	children,
	className,
	isLivePreviewImage = false,
	option: model,
	type = 'div',
	...rest
}) => {
	if (!model) {
		return null;
	}

	const fullProps = getImageDisplayOptions(model);

	if (type === 'img') {
		return (
			<div className={classNames('simple-image-img-container', className, { 'lp-tag': isLivePreviewImage })}>
				<img
					className={classNames('simple-image')}
					src={fullProps.url}
					style={{
						...rest.style,
						objectPosition: fullProps.position,
						objectFit: fullProps.fit,

						// Add max-* as styles
						width: addPx(model.max_width),
						height: addPx(model.max_height),
					}}
					alt={fullProps.alt_text}
					aria-label={fullProps.alt_text}
					{...model?.$?.uid}
				/>
			</div>
		);
	}

	return (
		<div
			className={classNames('simple-image', className, { 'lp-tag': isLivePreviewImage })}
			style={{
				...rest.style,
				backgroundImage: `url(${fullProps.url})`,
				backgroundPosition: fullProps.position,
				backgroundSize: fullProps.fit ?? 'cover',

				// Add max-* as styles
				width: addPx(model.max_width),
				height: addPx(model.max_height),
			}}
			aria-label={fullProps.alt_text}
			{...model?.$?.uid}
		>
			{children}
		</div>
	);
};

export default BynderSimpleImage;
